import type { FC } from "react"
import React, { useEffect, useState } from "react"
import {
  Text,
  Link,
  Stack,
  Flex,
  Box,
  HStack,
  Avatar,
  Icon,
} from "@chakra-ui/react"
import Linkify from "react-linkify"

import { useImportProposalData } from "createProposal/hooks/useImportProposalData"
import { ArrowUpRight } from "ui/components/icons/font-awesome/ArrowUpRight"
import { Heart } from "ui/components/icons/font-awesome/Heart"
import WarningBanner from "common/components/banners/WarningBanner"

type Props = {
  discourseURL: string
}

const DiscourseComments: FC<Props> = ({ discourseURL }) => {
  const [discourseComments, setDiscourseComments] = useState<any[]>([])
  const {
    proposalDetails,
    importData: importDetails,
    isLoading: isImportLoading,
    isError: isImportError,
  } = useImportProposalData()

  useEffect(() => {
    importDetails("discourse", discourseURL)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discourseURL])

  useEffect(() => {
    if (proposalDetails.comments) {
      setDiscourseComments(proposalDetails.comments)
    }
  }, [proposalDetails.comments])

  if (isImportLoading)
    return (
      <Stack pt={4} px={4}>
        <Text fontSize="md" fontWeight="regular">
          Loading...
        </Text>
      </Stack>
    )

  if (isImportError) {
    return (
      <Stack pt={4} px={4}>
        <WarningBanner>{`We couldn't find the proposal details`}</WarningBanner>
      </Stack>
    )
  }

  if (!discourseComments.length) {
    return (
      <Stack pt={4} px={4}>
        <Text fontSize="md" fontWeight="regular">
          No comments found
        </Text>
      </Stack>
    )
  }

  const renderContent = (content: string) => {
    const doc = new DOMParser().parseFromString(content, "text/html")
    const elements = Array.from(doc.body.childNodes)

    return elements.map((element, index) => {
      if (
        element.nodeName === "ASIDE" &&
        (element as Element).classList.contains("quote")
      ) {
        const quoteElement = element as Element
        const quotedUser =
          quoteElement.getAttribute("data-username") || "Unknown"
        const quoteContent =
          quoteElement.querySelector("blockquote")?.innerHTML || ""
        const quotedAvatar =
          quoteElement.querySelector("img")?.getAttribute("src") || ""

        return (
          <Stack
            key={index}
            borderLeft="4px solid"
            borderLeftColor="gray.200"
            my={4}
            p={4}
          >
            <HStack spacing={2}>
              <Avatar name={quotedUser} size="xs" src={quotedAvatar} />
              <Text color="gray.700" textStyle="body.regular.md">
                {quotedUser}:
              </Text>
            </HStack>
            <Text
              color="gray.600"
              dangerouslySetInnerHTML={{ __html: quoteContent }}
              textStyle="body.regular.md"
            />
          </Stack>
        )
      } else {
        const regularElement = element as HTMLElement

        if (regularElement.nodeName === "P") {
          regularElement.style.marginTop = "12px"
          regularElement.style.marginBottom = "12px"
        }

        return (
          <Text
            key={index}
            color="gray.900"
            dangerouslySetInnerHTML={{ __html: regularElement.outerHTML }}
            textStyle="body.regular.md"
          />
        )
      }
    })
  }

  return (
    <Stack spacing={{ base: 3, lg: 0 }}>
      {discourseComments.map((comment) => (
        <Stack
          key={comment.id}
          _notLast={{
            borderBottom: "1px solid",
            borderBottomColor: "gray.200",
          }}
          align="stretch"
          p={{ base: 0, lg: 10 }}
          spacing={4}
        >
          <Flex align="center" justify="space-between">
            <HStack spacing={3}>
              <Avatar
                name={comment.username}
                size="sm"
                src={`https://sjc6.discourse-cdn.com/standard17/${comment.avatar_template.replace(
                  "{size}",
                  "40",
                )}`}
              />
              <Stack spacing={0}>
                <Text color="gray.900" textStyle="body.regular.md">
                  {comment.username}
                </Text>
              </Stack>
            </HStack>
            <Text color="gray.500" textStyle="body.regular.md">
              {new Date(comment.created_at).toLocaleDateString()}
            </Text>
          </Flex>
          <Box>
            <Linkify
              componentDecorator={(
                href: string | undefined,
                text: string,
                key: React.Key | null | undefined,
              ) => (
                <Link
                  key={key}
                  isExternal
                  color="purple.500"
                  href={href}
                  textDecoration="underline"
                >
                  {text}
                </Link>
              )}
            >
              {renderContent(
                comment.cooked.slice(0, 750) +
                  (comment.cooked.length > 750 ? "..." : ""),
              )}
            </Linkify>
          </Box>
          <Flex justify="space-between">
            <Flex />
            <HStack spacing={3}>
              {comment.actions_summary[0]?.count ? (
                <HStack spacing={1}>
                  <Text color="gray.500" textStyle="body.regular.md">
                    {comment.actions_summary[0].count}
                  </Text>
                  <Icon
                    as={Heart}
                    color="gray.500"
                    fill="gray.500"
                    h={3.5}
                    w={3.5}
                  />
                </HStack>
              ) : null}
              <Link isExternal href={`${discourseURL}/${comment.post_number}`}>
                <HStack spacing={1}>
                  <Text color="gray.500" textStyle="body.regular.md">
                    Read full post
                  </Text>
                  <Icon as={ArrowUpRight} color="gray.500" h={3} w={3} />
                </HStack>
              </Link>
            </HStack>
          </Flex>
        </Stack>
      ))}

      {proposalDetails.comments.length >= 10 && (
        <Flex
          align="center"
          borderColor="gray.100"
          borderTopWidth="1px"
          h={12}
          justify="center"
          w="full"
        >
          <Link isExternal href={discourseURL}>
            <HStack pt={3} spacing={1}>
              <Text color="gray.700" textStyle="body.bold.md">
                View the full discussion
              </Text>
              <Icon as={ArrowUpRight} color="gray.700" h={4} w={4} />
            </HStack>
          </Link>
        </Flex>
      )}
    </Stack>
  )
}

export default DiscourseComments
